import React, { useState } from 'react';
import { routeDisplayName } from 'routes/routeDisplayName';
import {
  AttentionIcon,
  SortedDown,
  SortedUp,
  SortingArrows,
} from 'shared/components/ui/ProjectIcons';
import Pagination from 'shared/components/ui/Pagination';
import NoResults from 'shared/components/ui/NoResults';
import { HoverTooltip } from 'shared/components/ui/HoverTooltip';
import { page_size } from 'shared/api/apiUtils';
import { isEarthRangerTypeValue } from './earthRangerUrl';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// "https://site.pamdas.org/" → "site.pamdas.org". Falls back to the raw value
// when it isn't a parseable URL, so the cell is never blank.
const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

// Rows are route RECORDS (/v2/routes/): name + data_providers/destinations as
// integration summaries. Each row links to the route's own page.
type ConnectionDetailsModalTableProps = {
  routes: any[];
  requestSort: Function;
  loadingRoutes: boolean;
  onPageClicked: Function;
  totalCount: number;
  pageLoaded: boolean;
  take: number;
  offset: number;
  // The same table serves both roles a record can play, so the empty message and
  // the pager/sorting (server-side, and only the destination list is paged) vary.
  emptyLabel?: string;
  showPagination?: boolean;
  sortable?: boolean;
  // "Sending data to" table only: show the technologies on the receiving end
  // of each Route instead of its default-route column.
  showDestinationTech?: boolean;
};

const ConnectionDetailsModalTable = ({
  routes,
  requestSort,
  loadingRoutes,
  onPageClicked,
  totalCount,
  pageLoaded,
  take,
  offset,
  emptyLabel,
  showPagination = true,
  sortable = true,
  showDestinationTech = false,
}: ConnectionDetailsModalTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sortDirection, setSortDirection] = useState(true);
  const [sortByColumn, setSortByColumn] = useState('name');

  return (
    <div>
      <div className="flex w-full flex-col">
        <div className="overflow-x-auto">
          <table className="h-auto w-full table-fixed rounded-md border border-teal-t30 bg-white text-ui-text-primary-text transition-colors duration-300">
            <thead className="bg-teal-t100 px-3 pb-2 pt-3 text-left text-body-sm font-bold text-white transition-colors duration-300">
              <tr>
                <th className="w-1/3 p-3 transition-colors duration-300">
                  {t('Route Name')}
                  {sortable && (
                    <span
                      onClick={() => {
                        requestSort('name', sortDirection);
                        setSortDirection(!sortDirection);
                        setSortByColumn('name');
                      }}
                    >
                      {sortByColumn === 'name' ? (
                        sortDirection ? (
                          <SortedUp />
                        ) : (
                          <SortedDown />
                        )
                      ) : (
                        <SortingArrows />
                      )}
                    </span>
                  )}
                </th>
                {/* "Sending data to" lists Routes of THIS provider, so a
                                    Provider column says nothing; "Receiving data from" keeps
                                    it, and drops the default-route column that now duplicates
                                    the Route's own name. */}
                {/* The routes endpoint can't order by provider type,
                                    so the middle column is informational only. */}
                <th className="w-1/3 p-3 transition-colors duration-300">
                  {showDestinationTech ? t('Destinations') : t('Provider')}
                </th>
                <th className="w-1/3 p-3 transition-colors duration-300">
                  {t('Workspace')}
                  {sortable && (
                    <span
                      onClick={() => {
                        requestSort('owner__name', sortDirection);
                        setSortDirection(!sortDirection);
                        setSortByColumn('owner__name');
                      }}
                    >
                      {sortByColumn === 'owner__name' ? (
                        sortDirection ? (
                          <SortedUp />
                        ) : (
                          <SortedDown />
                        )
                      ) : (
                        <SortingArrows />
                      )}
                    </span>
                  )}
                </th>
              </tr>
            </thead>
            {loadingRoutes ? (
              <tbody>
                <tr>
                  <td className="p-3 transition-colors duration-300">...</td>
                </tr>
              </tbody>
            ) : routes.length < 1 ? (
              <tbody>
                <tr>
                  <td colSpan={3} className="py-16">
                    <NoResults
                      headerMsg={t('No Results Found')}
                      labelMsg={
                        emptyLabel || t('Not used as a destination yet.')
                      }
                    />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="cursor-pointer text-body-sm transition-colors duration-300">
                {routes.map((route: any) => {
                  // A route record with no provider at all is bad data (an
                  // orphan — its provider was deleted, or it was created via
                  // the API half-built). Its page can't load, so say
                  // something is odd instead of rendering blank cells.
                  const orphan =
                    !showDestinationTech &&
                    (route.data_providers || []).length === 0;
                  // Route URLs carry the provider INTEGRATION id, so a
                  // record with no provider has nowhere to go — the row
                  // stays informative rather than navigating to /routes/.
                  // `link_provider_id` wins when present: a reverse Route
                  // shown under the primary flow's name must open the
                  // Route its title promises, not its own provider.
                  const routeUrlId =
                    route.link_provider_id || route.data_providers?.[0]?.id;
                  // The tooltip wrapper is a <span>, which can't wrap a <tr> —
                  // wrap each cell's content instead so the message follows
                  // the mouse anywhere on the row.
                  const inRow = (content: React.ReactNode) =>
                    orphan ? (
                      <HoverTooltip
                        content={t(
                          'This Route has no data provider. Please contact support.',
                        )}
                        position="top"
                        portal
                        focusable={false}
                      >
                        {content}
                      </HoverTooltip>
                    ) : (
                      content
                    );
                  return (
                    <tr
                      key={route.id}
                      className="border-b border-teal-t30 bg-white align-top transition-colors duration-300 last:border-b-0 hover:bg-greyscale-11"
                      onClick={() => {
                        if (routeUrlId) navigate('/routes/' + routeUrlId);
                      }}
                    >
                      <td className="p-3 transition-colors duration-300">
                        {inRow(route.name || routeDisplayName(route))}
                      </td>
                      <td className="p-3 transition-colors duration-300">
                        {showDestinationTech
                          ? (() => {
                              // An EarthRanger destination is named by its site
                              // (site.pamdas.org), which says more than the
                              // technology every one of them shares.
                              const techs = Array.from(
                                new Set(
                                  (route.destinations || [])
                                    .map((d: any) =>
                                      d.base_url &&
                                      isEarthRangerTypeValue(d.type?.value)
                                        ? hostOf(d.base_url)
                                        : d.type?.name,
                                    )
                                    .filter(Boolean),
                                ),
                              );
                              // A Route with no destination sends nowhere, so "None"
                              // is a state to fix, not a value — same attention icon
                              // the forms use for a field that needs the user.
                              return techs.length > 0 ? (
                                techs.join(', ')
                              ) : (
                                <span className="flex flex-row items-center gap-1 text-ui-text-primary-text">
                                  {t('None')}
                                  <AttentionIcon />
                                </span>
                              );
                            })()
                          : orphan
                            ? inRow(
                                <span
                                  data-testid="DestinationDetailsModalTable-no-provider"
                                  className="flex flex-row items-center gap-1 text-ui-text-primary-text"
                                >
                                  {t('None')}
                                  <AttentionIcon />
                                </span>,
                              )
                            : route.data_providers?.[0]?.type?.name || ''}
                      </td>
                      <td className="p-3 transition-colors duration-300">
                        {inRow(
                          route.data_providers?.[0]?.owner?.name ||
                            route.owner?.name ||
                            '',
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {showPagination && totalCount > page_size && (
          <Pagination
            showText={true}
            handlePageClick={onPageClicked}
            pageCount={Math.ceil(totalCount / take)}
            resultStart={offset + 1}
            resultEnd={offset + take < totalCount ? offset + take : totalCount}
            totalCount={totalCount}
            take={take}
            loading={!pageLoaded}
            mainclassname="pt-2 w-full flex flex-row justify-between bg-greyscale-11 opacity-80 transition-colors duration-300"
          />
        )}
      </div>
    </div>
  );
};

export default ConnectionDetailsModalTable;
